/**
 * Edge Runtime Compatible WASM Loader for AgentShield
 *
 * This module provides a pre-built solution for loading WASM in Edge Runtime.
 * It requires the WASM file to be manually placed in the project.
 */

import type { NextRequest } from 'next/server';

// User must provide the WASM module via import
// This is intentionally not imported here to allow user control
export interface WasmModule {
  default: WebAssembly.Module;
}

export interface DetectionResult {
  isAgent: boolean;
  confidence: number;
  agent?: string;
  verificationMethod: 'cryptographic' | 'pattern';
  riskLevel?: 'low' | 'medium' | 'high' | 'critical';
  timestamp: string;
}

export interface AgentShieldConfig {
  wasmModule?: WebAssembly.Module;
  enableWasm?: boolean;
  onAgentDetected?: (result: DetectionResult) => void;
  blockAgents?: boolean;
  allowedAgents?: string[];
  debug?: boolean;
}

interface WasmExports {
  detect_agent(metadataPtr: number, metadataLen: number): number;
  get_version(): number;
  __wbindgen_malloc(size: number): number;
  __wbindgen_free(ptr: number, size: number): void;
  __wbindgen_realloc(ptr: number, oldSize: number, newSize: number): number;
  memory: WebAssembly.Memory;
}

class EdgeRuntimeAgentShield {
  private wasmInstance: WebAssembly.Instance | null = null;
  private wasmMemory: WebAssembly.Memory | null = null;
  private config: AgentShieldConfig;
  private initialized = false;

  constructor(config: AgentShieldConfig = {}) {
    this.config = config;
  }

  async init(wasmModule?: WebAssembly.Module): Promise<void> {
    if (this.initialized) return;

    const module = wasmModule || this.config.wasmModule;

    if (module && this.config.enableWasm !== false) {
      try {
        this.wasmMemory = new WebAssembly.Memory({ initial: 17, maximum: 256 });

        const imports = {
          wbg: {
            __wbindgen_throw: (ptr: number, len: number) => {
              throw new Error(this.readString(ptr, len));
            },
            __wbg_log_: (ptr: number, len: number) => {
              if (this.config.debug) {
                console.log(this.readString(ptr, len));
              }
            },
          },
          env: {
            memory: this.wasmMemory,
          },
        };

        this.wasmInstance = await WebAssembly.instantiate(module, imports);
        this.initialized = true;

        if (this.config.debug) {
          console.log('✅ AgentShield WASM initialized in Edge Runtime');
        }
      } catch (error) {
        console.warn('⚠️ WASM initialization failed, using pattern detection:', error);
        this.initialized = true;
      }
    } else {
      this.initialized = true;
    }
  }

  private readString(ptr: number, len: number): string {
    if (!this.wasmInstance || !this.wasmMemory) {
      throw new Error('WASM not initialized');
    }
    const memory = new Uint8Array(this.wasmMemory.buffer);
    const bytes = memory.slice(ptr, ptr + len);
    return new TextDecoder().decode(bytes);
  }

  private writeString(str: string): [number, number] {
    if (!this.wasmInstance) {
      throw new Error('WASM not initialized');
    }
    const exports = this.wasmInstance.exports as unknown as WasmExports;

    const encoded = new TextEncoder().encode(str);
    const ptr = exports.__wbindgen_malloc(encoded.length);

    const memory = new Uint8Array(exports.memory.buffer);
    memory.set(encoded, ptr);

    return [ptr, encoded.length];
  }

  async detect(request: NextRequest): Promise<DetectionResult> {
    if (!this.initialized) {
      await this.init();
    }

    const metadata = {
      userAgent: request.headers.get('user-agent') || '',
      ipAddress: request.ip || request.headers.get('x-forwarded-for')?.split(',')[0] || '',
      headers: Object.fromEntries(request.headers.entries()),
    };

    // Try WASM detection first
    if (this.wasmInstance && this.config.enableWasm !== false) {
      try {
        const exports = this.wasmInstance.exports as unknown as WasmExports;
        const [ptr, len] = this.writeString(JSON.stringify(metadata));

        const resultPtr = exports.detect_agent(ptr, len);
        // Read result with dynamic size
        let resultStr = '';
        let offset = 0;
        const memory = new Uint8Array((exports.memory as WebAssembly.Memory).buffer);

        // Read until we find the end of the JSON string
        while (offset < 4096) {
          // Max 4KB result
          const byte = memory[resultPtr + offset];
          if (byte === 0 || byte === undefined) break;
          offset++;
        }

        if (offset > 0) {
          resultStr = this.readString(resultPtr, offset);
        }

        exports.__wbindgen_free(ptr, len);
        if (offset > 0) {
          exports.__wbindgen_free(resultPtr, offset);
        }

        const result = JSON.parse(resultStr);

        const detection: DetectionResult = {
          ...result,
          verificationMethod: 'cryptographic',
          timestamp: new Date().toISOString(),
        };

        if (this.config.onAgentDetected && detection.isAgent) {
          this.config.onAgentDetected(detection);
        }

        return detection;
      } catch (error) {
        if (this.config.debug) {
          console.error('WASM detection failed:', error);
        }
        // Fall through to pattern detection
      }
    }

    // Fallback: Pattern-based detection
    return this.patternDetection(metadata);
  }

  private patternDetection(metadata: {
    userAgent: string;
    ipAddress: string;
    headers: Record<string, string>;
  }): DetectionResult {
    const userAgent = metadata.userAgent.toLowerCase();

    // Enhanced AI agent patterns with confidence scores
    const patterns = [
      // High confidence - explicit AI identifiers
      { pattern: /chatgpt-user/i, name: 'ChatGPT', confidence: 0.95 },
      { pattern: /claude-web/i, name: 'Claude', confidence: 0.95 },
      { pattern: /gpt-crawler/i, name: 'GPT Crawler', confidence: 0.95 },
      { pattern: /perplexitybot/i, name: 'Perplexity', confidence: 0.95 },
      { pattern: /perplexity-user/i, name: 'Perplexity', confidence: 0.95 },
      { pattern: /perplexity-ai/i, name: 'Perplexity', confidence: 0.95 },

      // Medium-high confidence - company identifiers
      { pattern: /anthropic/i, name: 'Anthropic', confidence: 0.9 },
      { pattern: /openai/i, name: 'OpenAI', confidence: 0.9 },

      // Medium confidence - product names
      { pattern: /copilot/i, name: 'GitHub Copilot', confidence: 0.85 },
      { pattern: /bard/i, name: 'Google Bard', confidence: 0.85 },
      { pattern: /gemini/i, name: 'Google Gemini', confidence: 0.85 },
      { pattern: /perplexity/i, name: 'Perplexity', confidence: 0.85 }, // Fallback
      { pattern: /you\.com/i, name: 'You.com', confidence: 0.8 },
      { pattern: /phind/i, name: 'Phind', confidence: 0.8 },
    ];

    // Check for suspicious headers
    const suspiciousHeaders = ['x-openai-', 'x-anthropic-', 'x-ai-', 'x-llm-', 'x-gpt-'];

    let headerBoost = 0;
    for (const [key] of Object.entries(metadata.headers)) {
      if (suspiciousHeaders.some((prefix) => key.toLowerCase().startsWith(prefix))) {
        headerBoost = 0.1;
        break;
      }
    }

    // Check patterns - patterns are ordered by confidence (highest first: 0.95, 0.90, 0.85...)
    // so breaking on first match returns the highest-confidence match
    for (const { pattern, name, confidence } of patterns) {
      if (pattern.test(userAgent)) {
        const finalConfidence = Math.min(confidence + headerBoost, 1.0);

        const result: DetectionResult = {
          isAgent: true,
          confidence: finalConfidence,
          agent: name,
          verificationMethod: 'pattern',
          riskLevel: finalConfidence > 0.9 ? 'high' : 'medium',
          timestamp: new Date().toISOString(),
        };

        if (this.config.onAgentDetected) {
          this.config.onAgentDetected(result);
        }

        return result;
      }
    }

    // Not detected
    return {
      isAgent: false,
      confidence: 0.85,
      verificationMethod: 'pattern',
      timestamp: new Date().toISOString(),
    };
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  getVerificationMethod(): 'cryptographic' | 'pattern' {
    return this.wasmInstance ? 'cryptographic' : 'pattern';
  }
}

/**
 * Factory function to create an AgentShield instance for Edge Runtime
 */
export function createEdgeAgentShield(config?: AgentShieldConfig) {
  return new EdgeRuntimeAgentShield(config);
}

/**
 * Singleton instance for simple usage
 */
let defaultInstance: EdgeRuntimeAgentShield | null = null;

export function getDefaultAgentShield(config?: AgentShieldConfig): EdgeRuntimeAgentShield {
  if (!defaultInstance) {
    defaultInstance = new EdgeRuntimeAgentShield(config);
  }
  return defaultInstance;
}
